import { useMemo, useState } from 'react';
import { useAppStore } from '../stores';
import {
  candidateAlreadyExists,
  existingChoreTitles,
  formatSchedule,
  ImportCandidate,
  InferenceConfidence,
} from '../domain';

/** UI-ready view of one import candidate. */
export interface ImportCandidateView extends ImportCandidate {
  scheduleLabel: string;
  confidenceLabel: string;
  /** Occurrence dates formatted for display, ascending. */
  occurrenceLabels: string[];
  /** Median gap rendered for the table, e.g. "7 days". Empty when nothing was measurable. */
  gapLabel: string;
  selected: boolean;
  /** Imported during this session — an exact key match, so it is not selectable again. */
  imported: boolean;
  /** A chore of this name already exists in the target household. A warning, not a block. */
  alreadyExists: boolean;
}

const CONFIDENCE_LABELS: Record<InferenceConfidence, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'Not inferred',
};

const toView = (
  candidate: ImportCandidate,
  selectedKeys: Set<string>,
  importedKeys: Set<string>,
  existingTitles: Set<string>,
): ImportCandidateView => {
  const { medianGapDays } = candidate.inference;
  return {
    ...candidate,
    scheduleLabel: formatSchedule(candidate.inference.schedule),
    confidenceLabel: CONFIDENCE_LABELS[candidate.inference.confidence],
    occurrenceLabels: candidate.occurrenceDates.map((date) => date.toLocaleDateString()),
    gapLabel: medianGapDays === null
      ? ''
      : `${Number.isInteger(medianGapDays) ? medianGapDays : medianGapDays.toFixed(1)} days`,
    selected: selectedKeys.has(candidate.key),
    imported: importedKeys.has(candidate.key),
    alreadyExists: candidateAlreadyExists(candidate, existingTitles),
  };
};

/**
 * Rows rendered per section. A Google account used as a to-do inbox can produce
 * thousands of candidates, and rendering them all makes the page unusable — the search
 * box is the way to reach anything past this.
 */
const RENDER_LIMIT = 200;

export const useGoogleTasksImport = () => {
  const {
    candidates, scanning, scanned, error, listCount, taskCount, truncatedLists, selectedKeys,
    importedKeys, importing, importResult, scan, reset, clearError, toggleSelection, setSelection,
    clearSelection, importSelected, dismissImportResult,
  } = useAppStore((state) => state.googleTasks);
  const households = useAppStore((state) => state.chores.households);

  const [targetHouseholdId, setTargetHouseholdId] = useState('');
  const [search, setSearch] = useState('');
  // Collapsed by default: on a busy account this section holds thousands of unique
  // todos that were never chores, and expanding it by default buries the useful rows.
  const [showOneOffs, setShowOneOffs] = useState(false);
  // On by default: among one-offs, a still-open task is a pending todo worth importing,
  // while a completed one is finished history. Only applied to the no-rhythm section —
  // a recurring chore with no instance currently open is still perfectly importable.
  const [openOneOffsOnly, setOpenOneOffsOnly] = useState(true);

  // Fall back to the first household so the import button is usable without the user
  // having to touch the picker when they only have one.
  const householdId = targetHouseholdId || households[0]?.household.id || '';

  // Matched against the *target* household, so switching the picker re-evaluates which
  // candidates would be duplicates. Includes completed one-offs: re-importing one would
  // still leave two chores behind.
  const existingTitles = useMemo(() => {
    const target = households.find((entry) => entry.household.id === householdId);
    return existingChoreTitles((target?.chores ?? []).map((entry) => entry.chore.name));
  }, [households, householdId]);

  const views = useMemo(() => {
    const selected = new Set(selectedKeys);
    const imported = new Set(importedKeys);
    return candidates.map((candidate) => toView(candidate, selected, imported, existingTitles));
  }, [candidates, selectedKeys, importedKeys, existingTitles]);

  const matching = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return views;
    return views.filter((candidate) => candidate.title.toLowerCase().includes(term));
  }, [views, search]);

  // Split so the UI can lead with what the inference actually found, and keep the
  // one-offs — which need a schedule chosen by hand — visually separate.
  const recurring = useMemo(
    () => matching.filter((candidate) => candidate.inference.confidence !== 'none'),
    [matching],
  );
  const allOneOffs = useMemo(
    () => matching.filter((candidate) => candidate.inference.confidence === 'none'),
    [matching],
  );
  const openOneOffs = useMemo(
    () => allOneOffs.filter((candidate) => candidate.hasOpenInstance),
    [allOneOffs],
  );
  const oneOffs = openOneOffsOnly ? openOneOffs : allOneOffs;

  const selectedCount = selectedKeys.length;
  const canImport = selectedCount > 0 && Boolean(householdId) && !importing;
  /** Selected rows that would duplicate an existing chore — surfaced as a warning before import. */
  const selectedDuplicateCount = views.filter((c) => c.selected && c.alreadyExists).length;

  const importToTarget = () => importSelected(householdId);

  /**
   * Bulk select skips likely duplicates so a careless "select all" can't sweep them in;
   * bulk deselect clears the whole section, including any duplicate ticked by hand.
   */
  const toggleSection = (section: ImportCandidateView[], selected: boolean) => {
    const keys = selected
      ? section.filter((c) => !c.imported && !c.alreadyExists).map((c) => c.key)
      : section.map((c) => c.key);
    setSelection(keys, selected);
  };

  const sectionState = (section: ImportCandidateView[]) => {
    const selectable = section.filter((c) => !c.imported && !c.alreadyExists);
    const chosen = selectable.filter((c) => c.selected);
    return {
      allSelected: selectable.length > 0 && chosen.length === selectable.length,
      someSelected: chosen.length > 0 && chosen.length < selectable.length,
      hasSelectable: selectable.length > 0,
    };
  };

  return {
    candidates: views,
    recurring: recurring.slice(0, RENDER_LIMIT),
    oneOffs: oneOffs.slice(0, RENDER_LIMIT),
    // Full counts, so the UI can say how much the render cap is hiding.
    recurringTotal: recurring.length,
    oneOffsTotal: oneOffs.length,
    /** Every one-off matching the search, open or not — the denominator for the filter label. */
    oneOffsAllTotal: allOneOffs.length,
    oneOffsOpenTotal: openOneOffs.length,
    renderLimit: RENDER_LIMIT,
    search,
    setSearch,
    showOneOffs,
    setShowOneOffs,
    openOneOffsOnly,
    setOpenOneOffsOnly,
    truncatedLists,
    scanning,
    scanned,
    error,
    listCount,
    taskCount,
    scan,
    reset,
    clearError,
    households,
    householdId,
    setTargetHouseholdId,
    selectedCount,
    selectedDuplicateCount,
    toggleSelection,
    toggleSection,
    sectionState,
    clearSelection,
    canImport,
    importing,
    importResult,
    importToTarget,
    dismissImportResult,
  };
};
